import React from "react";
import "./style.scss";

type Tab = {
  id: string;
  label: React.ReactNode | string;
  icon?: React.ReactNode;
  disabled?: boolean;
  /**
   * Looks unavailable (muted) but stays clickable — e.g. Open Actions
   * Disabled: the Actions tab must still open so users can read why and
   * reach read-only history.
   */
  inert?: boolean;
  isHide?: boolean;
  count?: number | string;
  badgeType?:
    | "primary"
    | "success"
    | "danger"
    | "success-green"
    | "warning"
    | "info"
    | "teal"
    | "primary-border"
    | "custom"
    | "completed-green"
    | "completed-blue"
    | "short-assessment"
    | "long-assessment-purple";
};

type TabsProps = {
  tabs: Tab[];
  activeTab: string | null;
  className?: string;
  onTabChange: (id: any) => void;
  variant:
    | "pill-filled"
    | "pill-outline"
    | "tag-rounded"
    | "tab-underline"
    | "text-link"
    | "tab-segmented"
    | "secondary-segmented"
    | "segmented"
    /** Level Mix Formal/Team grid (My Assessments → Levels Mix). */
    | "kind-grid";
  /** For `secondary-segmented` only: `fill` stretches to container width (default); `fit` hugs tab labels. */
  segmentedLayout?: "fit" | "fill";
  /** Allow horizontal scrolling instead of wrapping (applies to `tab-underline` variant). */
  scrollable?: boolean;
};

const TabsComponent: React.FC<TabsProps> = ({
  tabs,
  activeTab,
  onTabChange,
  variant,
  className,
  segmentedLayout = "fill",
  scrollable = false,
}) => {
  const visibleTabs = tabs.filter(tab => !tab.isHide);

  // Get badge styling based on active state and badgeType
  const getBadgeStyles = (
    isActive: boolean,
    badgeType?: string,
    variant?: string
  ) => {
    // For tag-rounded variant, keep badge styling consistent regardless of active state
    if (variant === "tag-rounded") {
      return "bg-gray-200 text-gray-700";
    }
    // On purple segmented tabs, active badge uses inverted colors for contrast
    if (variant === "tab-segmented" && isActive) {
      return "bg-white text-[#974ae4]";
    }
    if (isActive) {
      // Active tab: purple background with white text
      return "bg-[#974ae4] text-white";
    } else {
      // Inactive tab: light gray background with dark gray text
      return "bg-gray-200 text-gray-700";
    }
  };

  // Tailwind classes for tab-segmented variant
  const getTabSegmentedClasses = (
    isActive: boolean,
    isDisabled: boolean,
    isInert = false
  ) => {
    const baseClasses =
      "flex items-center gap-2 px-4 py-2 text-sm font-medium whitespace-nowrap rounded-full transition-all duration-200 border-none outline-none focus:outline-none focus:shadow-none";

    if (isDisabled) {
      return `${baseClasses} text-black opacity-50 cursor-not-allowed`;
    }

    if (isInert) {
      // Muted “off” look; still clickable so the tab can explain why.
      if (isActive) {
        return `${baseClasses} bg-gray-200 text-gray-500 cursor-pointer`;
      }
      return `${baseClasses} text-gray-400 opacity-60 hover:bg-gray-100 hover:text-gray-500 cursor-pointer`;
    }

    if (isActive) {
      return `${baseClasses} bg-[#974ae4] text-white`;
    }

    return `${baseClasses} text-black hover:bg-[rgba(151,74,228,0.05)] cursor-pointer`;
  };

  const getSecondarySegmentedClasses = (
    isActive: boolean,
    isDisabled: boolean,
    pillShape = false
  ) => {
    const baseClasses = pillShape
      ? "inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-sm font-medium transition-all border whitespace-nowrap"
      : "inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium transition-all border whitespace-nowrap";

    if (isDisabled) {
      return `${baseClasses} border-transparent bg-transparent text-gray-400 cursor-not-allowed`;
    }

    if (isActive) {
      return `${baseClasses} border border-[#974ae4] bg-white text-[#7a36c4] shadow-sm`;
    }

    return `${baseClasses} border border-transparent bg-transparent text-gray-600 hover:bg-white hover:text-gray-800`;
  };

  if (variant === "tab-segmented") {
    return (
      <nav
        className={`${className || ""} flex w-fit gap-0 bg-purple-50 border-2 border-[rgba(151,74,228,0.2)] rounded-[2.625rem] p-1 overflow-hidden mb-0`}
      >
        {visibleTabs.map(tab => {
          const isActive = activeTab === tab.id;
          const hasCount = tab.count !== undefined && tab.count !== null;
          const isInert = !!tab.inert && !tab.disabled;
          return (
            <div key={tab.id} className="m-0 list-none">
              <button
                type="button"
                className={`${getTabSegmentedClasses(isActive, !!tab.disabled, isInert)} text-gray-500 w-full justify-center`}
                disabled={tab.disabled}
                aria-disabled={isInert || undefined}
                onClick={() => !tab.disabled && onTabChange(tab.id)}
              >
                {tab.icon && (
                  <span
                    className={`flex-shrink-0 [&>svg]:w-[18px] [&>svg]:h-[18px] [&>svg]:transition-colors [&>svg]:duration-200 ${
                      isActive && !isInert
                        ? "[&>svg]:text-white"
                        : "[&>svg]:text-current"
                    }`}
                  >
                    {tab.icon}
                  </span>
                )}
                {tab.label}
                {hasCount && (
                  <span
                    className={`inline-flex items-center justify-center relative min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-medium leading-none ${getBadgeStyles(isActive, tab.badgeType, variant)}`}
                  >
                    {tab.count?.toString() || "0"}
                  </span>
                )}
              </button>
            </div>
          );
        })}
      </nav>
    );
  }

  if (variant === "kind-grid") {
    return (
      <nav
        className={`grid grid-cols-2 overflow-visible rounded-md border border-gray-200 bg-white text-sm font-medium ${className || ""}`}
      >
        {visibleTabs.map((tab, index, tabs) => {
          const isActive = activeTab === tab.id;
          const isFirst = index === 0;
          const isLast = index === tabs.length - 1;
          return (
            <button
              key={tab.id}
              type="button"
              disabled={tab.disabled}
              onClick={() => !tab.disabled && onTabChange(tab.id)}
              className={`px-3 py-2 transition-colors ${
                isFirst ? "rounded-l-[5px]" : ""
              } ${isLast ? "rounded-r-[5px]" : ""} ${
                isActive
                  ? "bg-[#f5edfc] text-[#974ae4]"
                  : "bg-white text-[#4b5563] hover:bg-[#f3f4f6]"
              } ${tab.disabled ? "cursor-not-allowed opacity-50" : ""}`}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>
    );
  }

  if (variant === "secondary-segmented") {
    const isFit = segmentedLayout === "fit";
    const usePillShape = className?.includes("rounded-full");
    return (
      <nav
        className={`${className || ""} ${
          isFit ? "inline-flex w-fit max-w-full shrink-0" : "flex w-full"
        } items-center gap-1 ${
          usePillShape ? "rounded-full" : "rounded-xl"
        } border border-gray-200 bg-gray-50 p-1`}
      >
        {visibleTabs.map((tab, index) => {
          const isActive = activeTab === tab.id;
          const prev = visibleTabs[index - 1];
          const prevActive = prev ? activeTab === prev.id : false;
          const showDivider = index > 0 && !isActive && !prevActive;
          return (
            <React.Fragment key={tab.id}>
              {showDivider && (
                <span
                  aria-hidden
                  className="h-4 w-px flex-shrink-0 self-center bg-gray-300"
                />
              )}
              {!showDivider && index > 0 && (
                <span
                  aria-hidden
                  className="h-4 w-px flex-shrink-0 self-center bg-transparent"
                />
              )}
              <div
                className={`m-0 list-none ${isFit ? "shrink-0" : "flex-1 min-w-0"}`}
              >
                <button
                  type="button"
                  className={`${getSecondarySegmentedClasses(isActive, !!tab.disabled, usePillShape)} ${isFit ? "" : "w-full"}`}
                  disabled={tab.disabled}
                  onClick={() => !tab.disabled && onTabChange(tab.id)}
                >
                  {tab.icon && (
                    <span
                      className={`flex-shrink-0 [&>svg]:transition-colors [&>svg]:duration-200 ${isActive ? "[&>svg]:text-[#7a36c4]" : "[&>svg]:text-current"}`}
                    >
                      {tab.icon}
                    </span>
                  )}
                  <span>{tab.label}</span>
                </button>
              </div>
            </React.Fragment>
          );
        })}
      </nav>
    );
  }

  return (
    <nav
      className={`${className || ""} custom-tabs ${variant}${scrollable && variant === "tab-underline" ? " flex-nowrap overflow-x-auto library-nav-scroll" : ""}`}
    >
      {visibleTabs.map(tab => {
        const isActive = activeTab === tab.id;
        const hasCount = tab.count !== undefined && tab.count !== null;
        const showCount =
          (variant === "tab-underline" ||
            variant === "tag-rounded" ||
            variant === "pill-outline") &&
          hasCount;

        return (
          <div key={tab.id}>
            <button
              type="button"
              className={`tab-item ${isActive ? "active" : "text-gray-500"} ${showCount ? "tab-with-count" : ""}`}
              disabled={tab.disabled}
              onClick={() => !tab.disabled && onTabChange(tab.id)}
            >
              {tab.icon && (
                <span
                  className={
                    "flex-shrink-0 [&>svg]:w-[18px] [&>svg]:h-[18px] [&>svg]:transition-colors [&>svg]:duration-200"
                  }
                >
                  {tab.icon}
                </span>
              )}
              <span
                className={`tab-label inline-block align-middle leading-tight ${!isActive ? "text-gray-500" : ""}`}
              >
                {tab.label}
              </span>
              {showCount && (
                <span
                  className={`inline-flex items-center justify-center relative min-w-[20px] h-5 px-0.5 rounded-full text-[11px] font-medium leading-none ${getBadgeStyles(isActive, tab.badgeType, variant)}`}
                >
                  {tab.count?.toString() || "0"}
                </span>
              )}
            </button>
          </div>
        );
      })}
    </nav>
  );
};

export default TabsComponent;
